import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { AuthProvider, useAuth } from '@/auth/AuthContext'
import { Layout } from '@/components/Layout'
import { ToastProvider } from '@/components/Toast'
import { LoadingBlock } from '@/components/ui'
import { DashboardPage } from '@/pages/DashboardPage'
import { LoginPage } from '@/pages/LoginPage'
import { ArchivePage } from '@/pages/ArchivePage'
import { CategoryPage } from '@/pages/CategoryPage'
import { ConsultingPage } from '@/pages/ConsultingPage'
import { ConsultingClientsPage } from '@/pages/ConsultingClientsPage'
import { ConsultingInvoicesPage } from '@/pages/ConsultingInvoicesPage'
import { NotesPage } from '@/pages/NotesPage'
import { NotFoundPage } from '@/pages/Placeholder'
import { SettingsPage } from '@/pages/SettingsPage'

/**
 * Lazily loaded: this page pulls in TipTap and ProseMirror, which are most of
 * the bundle. The dashboard is what loads on every visit and has no use for an
 * editor, so it should not pay for one.
 */
const ItemDetailPage = lazy(() =>
  import('@/pages/ItemDetailPage').then((m) => ({ default: m.ItemDetailPage })),
)

/** Also lazy: it carries the same TipTap editor as the item page, and the two
 *  share that chunk. */
const NoteDetailPage = lazy(() =>
  import('@/pages/NoteDetailPage').then((m) => ({ default: m.NoteDetailPage })),
)

/**
 * Gate for everything behind sign-in.
 *
 * While the initial session check is in flight it renders a loading state
 * rather than the login page: redirecting first would flash the login screen at
 * an already-authenticated user on every refresh.
 */
function RequireAuth() {
  const { user, loading } = useAuth()
  if (loading) return <LoadingBlock label="Checking your session" />
  if (!user) return <Navigate to="/login" replace />
  return <Outlet />
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAuth />}>
              <Route element={<Layout />}>
                <Route index element={<DashboardPage />} />
                <Route
                  path="items/:itemId"
                  element={
                    <Suspense fallback={<LoadingBlock label="Loading item" />}>
                      <ItemDetailPage />
                    </Suspense>
                  }
                />
                <Route path="categories/:categoryId" element={<CategoryPage />} />
                <Route path="consulting" element={<ConsultingPage />} />
                <Route path="consulting/clients" element={<ConsultingClientsPage />} />
                <Route path="consulting/invoices" element={<ConsultingInvoicesPage />} />
                <Route path="notes" element={<NotesPage />} />
                <Route
                  path="notes/:noteId"
                  element={
                    <Suspense fallback={<LoadingBlock label="Loading note" />}>
                      <NoteDetailPage />
                    </Suspense>
                  }
                />
                <Route path="archive" element={<ArchivePage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
